// Scoring: apply an answer key to a raw response. Deliberately separate from the
// engine — the engine reads the sheet; scoring interprets it against a key.
// Keeping them apart makes re-scoring trivial (edit the key → re-score from stored
// raw answers) and lets us audit reads independent of keys.
import type { AnswerKeyRecord, ResultItem } from '../db/schema.js';

// ---- key access (plain OR TESTIFY hashed) --------------------------------
// A TESTIFY key (§13.3) stores, per item, a key_hash per option: '0' = wrong, a
// salted SHA-256 = correct. Scoring is a pure zero/non-zero check — we NEVER hash
// anything client-side and never compare hashes to each other. For a local key we
// compare the chosen letter to the stored correct letter. Both go through these
// two helpers so the rest of the app doesn't care which kind of key it is.

/** The item numbers a key defines answers for (works for plain and hashed keys). */
export function keyItems(key: AnswerKeyRecord): number[] {
  const src = key.hashedAnswers ?? key.answers;
  return Object.keys(src).map(Number).sort((a, b) => a - b);
}

/** Is this chosen option correct for item q? Hashed → key_hash non-zero. */
export function isCorrectChoice(key: AnswerKeyRecord, q: number, chosen: string | null): boolean {
  if (chosen == null) return false;
  if (key.hashedAnswers) return (key.hashedAnswers[q]?.[chosen] ?? '0') !== '0';
  return key.answers?.[q] === chosen;
}

/** The correct option letter for item q, derived from a hashed key if needed
 *  (the single non-zero choice). Used internally for local analytics only. */
export function correctOptionFor(key: AnswerKeyRecord, q: number): string | null {
  if (key.hashedAnswers) {
    const row = key.hashedAnswers[q];
    if (!row) return null;
    for (const opt of Object.keys(row)) if ((row[opt] ?? '0') !== '0') return opt;
    return null;
  }
  return key.answers?.[q] ?? null;
}

export type Outcome = 'correct' | 'wrong' | 'blank' | 'double' | 'unscored';

/** Does this key define an answer for item q? (works for plain and hashed keys) */
export function keyHasItem(key: AnswerKeyRecord, q: number): boolean {
  const src = key.hashedAnswers ?? key.answers;
  return !!src && Object.prototype.hasOwnProperty.call(src, q);
}

/**
 * Classify one raw read against a key. This is the per-item half of
 * `scoreResponse` — pulled out so the review grid's outcome markers are the very
 * same verdict the score is built from and can never drift from it. Ambiguity is
 * preserved: a double-mark stays `double` and an empty item stays `blank`; neither
 * is folded into `wrong`, even when one of a double's marks matches the key.
 * No key (or no key entry for this item) → `unscored`, never a guess.
 */
export function classifyOutcome(
  key: AnswerKeyRecord | null | undefined,
  q: number,
  read?: { chosen: string | null; flags: readonly string[] } | null,
): Outcome {
  if (!key || !keyHasItem(key, q)) return 'unscored';
  if (read?.flags.includes('double')) return 'double';
  if (!read || read.chosen === null || read.flags.includes('blank')) return 'blank';
  return isCorrectChoice(key, q, read.chosen) ? 'correct' : 'wrong';
}

export interface ScoredItem {
  q: number;
  chosen: string | null;
  key: string | null;
  outcome: Outcome;
  points: number; // awarded
  maxPoints: number;
  /** needs a human to look (ambiguous read: double/blank/light). */
  review: boolean;
}

export interface ScoreResult {
  score: number;
  maxScore: number;
  items: ScoredItem[];
  /** items a teacher should review before trusting the score. */
  reviewItems: number[];
}

/**
 * Score a raw read against a key. Ambiguous reads (double-marks, blanks, and
 * low-confidence "review" flags) score 0 and are surfaced for review rather than
 * guessed — a double-mark is never silently counted as correct even if one of
 * the marked options happens to match the key.
 */
export function scoreResponse(answers: ResultItem[], key: AnswerKeyRecord): ScoreResult {
  const byQ = new Map(answers.map((a) => [a.q, a]));
  const items: ScoredItem[] = [];
  const reviewItems: number[] = [];
  let score = 0;
  let maxScore = 0;

  // score every item that the key defines an answer for
  for (const q of keyItems(key)) {
    const keyOpt = correctOptionFor(key, q);
    const maxPoints = key.points?.[q] ?? 1;
    maxScore += maxPoints;

    const read = byQ.get(q);
    const outcome = classifyOutcome(key, q, read);
    // 'review' isn't an outcome (a plain wrong answer can still be a shaky read),
    // so the review flag is checked alongside the classified outcome.
    const review = outcome === 'double' || outcome === 'blank' || (read?.flags.includes('review') ?? false);
    if (review) reviewItems.push(q);

    const points = outcome === 'correct' ? maxPoints : 0;
    score += points;
    items.push({ q, chosen: read?.chosen ?? null, key: keyOpt, outcome, points, maxPoints, review });
  }

  return { score, maxScore, items, reviewItems: reviewItems.sort((a, b) => a - b) };
}
